/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
import React, { useState, useContext, useEffect, useRef } from 'react';
import { Button, Modal, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { useHistory } from 'react-router-dom';
import { FDContext } from 'contexts/Map/fdStore';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import * as turf from '@turf/turf'; // Add this import for area calculation

import {
  createSubscription as createSubscriptionAPI,
  createSubscriptionFdData as createSubscriptionFdDataAPI,
} from 'api/fieldDelineation/subscriptions';
import ProgressModal from 'components/ProgressModal';
import Toast from 'components/Toast';

const useStyles = makeStyles({
  mx8: {
    margin: '0 8px',
    marginTop: '20px',
  },
  modalContent: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '600px',
    height: '300px',
    backgroundColor: '#fff',
    border: '2px solid',
    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
    padding: '16px 32px 24px',
    outline: 'none',
    alignContent: 'center',
    alignItems: 'center',
    textAlign: 'center',
    borderRadius: '15px',
  },
  modalHeader: {
    marginBottom: '20px',
  },
  modalBody: {
    marginBottom: '30px',
  },
  progressContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: '20px',
  },
  stepCircle: {
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#e0e0e0',
    margin: '0 10px',
    transition: 'background-color 0.3s ease',
  },
  activeStep: {
    backgroundColor: '#1976d2',
    color: 'white',
  },
  completedStep: {
    backgroundColor: '#4caf50',
    color: 'white',
  },
  arrow: {
    color: '#e0e0e0',
    transition: 'color 0.3s ease',
  },
  activeArrow: {
    color: '#1976d2',
  },
  progressBar: {
    width: '100%',
    height: '8px',
    backgroundColor: '#e0e0e0',
    borderRadius: '4px',
    overflow: 'hidden',
    marginTop: '20px',
  },
  progressFill: {
    height: '100%',
    backgroundColor: '#1976d2',
    transition: 'width 0.3s ease',
  },
});

const CreateSubscription = ({
  name,
  formData,
  apiType,
  result,
  isModalOpenSub,
  setIsModalOpenSub,
  setIsOrderClicked,
  onClose,
  modalTitle,
  modalDescription,
  disabled = false,
}) => {
  const classes = useStyles();
  const [progress, setProgress] = useState(0);
  const [currentStep, setCurrentStep] = useState(1);
  const [isProcessing, setIsProcessing] = useState(false);
  const [toastAlertOpts, setToastAlertOpts] = useState({
    show: false,
    duration: 5000,
    severity: 'success',
    message: '',
  });
  const [errorMessage, setErrorMessage] = useState('');
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const history = useHistory();

  // Track if we've already shown an alert to prevent duplicates
  const alertShownRef = useRef(false);

  const wait = (time) =>
    new Promise((resolve) => {
      setTimeout(resolve, time);
    });

  // Function to calculate areas from result.responseJson
  const calculateAreas = (responseJson) => {
    const areas = [];

    if (!responseJson) {
      return areas;
    }

    let featuresArray = [];

    // Handle different response formats
    if (responseJson.features) {
      featuresArray = responseJson.features;
    } else if (responseJson.geometry) {
      featuresArray = [responseJson];
    }

    // Calculate areas for each feature
    featuresArray.forEach((feature) => {
      if (feature.properties?.area) {
        // Area already exists in properties, convert from m² to hectares
        areas.push(feature.properties.area / 10000);
      } else if (feature.geometry && feature.geometry.type === 'Polygon') {
        // Calculate area using turf and convert from m² to hectares
        areas.push(turf.area(feature.geometry) / 10000);
      }
    });

    return areas;
  };

  // Handle close attempts
  const handleCloseAttempt = () => {
    if (isProcessing) {
      // Only show alert if we haven't shown one recently
      if (!alertShownRef.current) {
        alertShownRef.current = true;
        alert(
          'Please wait while your order is being processed. Do not close this window.'
        );
        // Reset the flag after a short delay to allow future alerts if needed
        setTimeout(() => {
          alertShownRef.current = false;
        }, 2000);
      }
      return;
    }
    // Allow closing only if not processing
    setIsModalOpenSub(false);
    if (onClose) {
      onClose();
    }
  };

  // Prevent browser window/tab closing and handle navigation
  useEffect(() => {
    const handleBeforeUnload = (event) => {
      if (isProcessing) {
        // Modern browsers will show their own confirmation dialog
        // We don't need to show a custom alert here as it would be redundant
        event.preventDefault();
        const message =
          'Your order is being processed. Are you sure you want to leave?';
        // eslint-disable-next-line no-param-reassign
        event.returnValue = message;
        return message;
      }
      return undefined;
    };

    // Handle browser back button and navigation
    const handlePopState = (event) => {
      if (isProcessing) {
        // Push the current state back to prevent navigation
        window.history.pushState(null, '', window.location.href);

        // Show alert only if we haven't shown one recently
        if (!alertShownRef.current) {
          alertShownRef.current = true;
          alert(
            'Please wait while your order is being processed. You cannot navigate away at this time.'
          );
          setTimeout(() => {
            alertShownRef.current = false;
          }, 2000);
        }
      }
    };

    if (isProcessing) {
      // Add state to history to intercept back button
      window.history.pushState(null, '', window.location.href);

      window.addEventListener('beforeunload', handleBeforeUnload);
      window.addEventListener('popstate', handlePopState);
    }

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('popstate', handlePopState);
    };
  }, [isProcessing]);

  // Monitor network status
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Reset alert flag when processing starts/stops
  useEffect(() => {
    if (!isProcessing) {
      alertShownRef.current = false;
    }
  }, [isProcessing]);

  // Define createSubscription before using it in useEffect
  const createSubscription = async () => {
    if (disabled) {
      setToastAlertOpts({
        show: true,
        severity: 'error',
        message: 'Insufficient credits available to create subscription',
        duration: 5000,
      });
      setIsProcessing(false);
      setIsModalOpenSub(false);
      if (onClose) {
        onClose();
      }
      return;
    }

    let retryCount = 0;
    const maxRetries = 2;

    const attemptCreateSubscription = async () => {
      try {
        setProgress(0);
        setCurrentStep(1);
        setIsProcessing(true);
        setErrorMessage('');

        // Calculate areas from result.responseJson
        const areas = calculateAreas(result?.responseJson);

        // First step - Creating subscription
        const subscriptionParameters = {
          name,
          props: {
            ...formData,
            apiType,
            areas, // Add calculated areas to props
          },
        };

        // console.log('subscriptionParameters', subscriptionParameters);

        // Simulate or make actual API call
        await wait(500);

        let subscription;
        try {
          subscription = await createSubscriptionAPI(subscriptionParameters);
        } catch (subscriptionError) {
          // Handle specific subscription creation errors
          if (
            subscriptionError?.response?.status >= 500 &&
            retryCount < maxRetries
          ) {
            retryCount += 1;
            setToastAlertOpts({
              show: true,
              severity: 'warning',
              message: `Network error occurred. Retrying... (${retryCount}/${maxRetries})`,
              duration: 3000,
            });
            await wait(2000); // Wait before retry
            return attemptCreateSubscription(); // Recursive retry
          }
          throw subscriptionError; // Re-throw if not retryable or max retries reached
        }

        setProgress(40);
        setCurrentStep(2);

        // Second step - Creating FD data
        // console.log('testing subscription response: ', subscription);

        const fdData = {
          subscriptionId: subscription?.id,
          // subscriptionId: '12345',
          userId: subscription?.userId,
          token: formData.token,
          apiType,
          props: result,
          datalayerType: formData.dataLayerFdType,
        };

        await wait(2500);

        try {
          await createSubscriptionFdDataAPI(fdData);
        } catch (fdDataError) {
          // Handle FD data creation errors
          if (fdDataError?.response?.status >= 500 && retryCount < maxRetries) {
            retryCount += 1;
            setToastAlertOpts({
              show: true,
              severity: 'warning',
              message: `Network error occurred. Retrying... (${retryCount}/${maxRetries})`,
              duration: 3000,
            });
            await wait(2000);
            // For FD data errors, we retry just this step since subscription was created
            await createSubscriptionFdDataAPI(fdData);
          } else {
            throw fdDataError;
          }
        }

        setProgress(80);
        await wait(500);
        setProgress(100);

        // Show success toast
        setToastAlertOpts({
          show: true,
          severity: 'success',
          message: 'Subscription created successfully',
          duration: 5000,
        });

        // Wait before closing
        await wait(750);
        setIsProcessing(false);
        setIsOrderClicked(true);
        setIsModalOpenSub(false);

        if (onClose) {
          onClose();
        }

        // Uncomment for navigation
        // const encodedName = encodeURIComponent(name);
        // history.push(`/delineated-fields/subscriptions/56sjhdgfdhdvhndsjv78?name=${encodedName}`);
      } catch (err) {
        // console.log('error in subscription creation:', err);
        setIsProcessing(false);

        let errorMessageHere = 'Error creating subscription';

        // Handle different types of network errors
        if (!navigator.onLine) {
          errorMessageHere =
            'No internet connection. Please check your network and try again.';
        } else if (
          err?.code === 'NETWORK_ERROR' ||
          err?.message?.includes('Network Error')
        ) {
          errorMessageHere = 'Network error occurred. Please try again.';
        } else if (err?.response?.status >= 500) {
          errorMessageHere = 'Server error occurred. Please try again later.';
        } else if (err?.response?.status === 408) {
          errorMessageHere = 'Request timeout. Please try again.';
        } else if (err?.response?.data?.message) {
          errorMessageHere = err.response.data.message;
        }

        setToastAlertOpts({
          show: true,
          severity: 'error',
          message: errorMessageHere,
          duration: 8000, // Longer duration for error messages
        });

        setErrorMessage(errorMessageHere);
        setIsModalOpenSub(false);
      }
      return undefined;
    };

    await attemptCreateSubscription();
  };

  // Use createSubscription in useEffect after it's defined
  useEffect(() => {
    if (isModalOpenSub && name && result !== undefined && !disabled) {
      createSubscription();
    } else if (isModalOpenSub && disabled) {
      // Show error message for insufficient credits
      setToastAlertOpts({
        show: true,
        severity: 'error',
        message: 'Insufficient credits available to create subscription',
        duration: 5000,
      });
      setIsModalOpenSub(false);
      if (onClose) {
        onClose();
      }
    }
  }, [isModalOpenSub, disabled]);

  return (
    <div>
      <Modal
        open={isModalOpenSub}
        onClose={handleCloseAttempt}
        aria-labelledby="create-subscription-modal"
        aria-describedby="create-subscription-modal-description"
        disableEscapeKeyDown={isProcessing}
        // Prevent clicking outside to close when processing
        componentsProps={{
          backdrop: {
            onClick: (event) => {
              if (isProcessing) {
                event.stopPropagation();
                // Use the same alert logic to prevent duplicates
                if (!alertShownRef.current) {
                  alertShownRef.current = true;
                  alert('Please wait while your order is being processed.');
                  setTimeout(() => {
                    alertShownRef.current = false;
                  }, 2000);
                }
              }
            },
          },
        }}
      >
        <div
          className={classes.modalContent}
          onClick={(event) => {
            // Prevent any clicks inside modal from doing anything when processing
            if (isProcessing) {
              event.stopPropagation();
            }
          }}
          onKeyDown={(event) => {
            // Handle keyboard events for accessibility
            if (isProcessing && (event.key === 'Enter' || event.key === ' ')) {
              event.stopPropagation();
            }
          }}
          role="dialog"
        >
          <Typography variant="h6" className={classes.modalHeader} gutterBottom>
            {modalTitle}
          </Typography>
          <Typography
            variant="body1"
            className={classes.modalBody}
            gutterBottom
          >
            {modalDescription}
          </Typography>

          <div className={classes.progressContainer}>
            <div
              className={`${classes.stepCircle} ${
                currentStep >= 1 ? classes.activeStep : ''
              }`}
            >
              1
            </div>
            <ArrowForwardIcon
              className={`${classes.arrow} ${
                progress >= 50 ? classes.activeArrow : ''
              }`}
            />
            <div
              className={`${classes.stepCircle} ${
                currentStep >= 2 ? classes.activeStep : ''
              } ${progress === 100 ? classes.completedStep : ''}`}
            >
              2
            </div>
          </div>

          <div className={classes.progressBar}>
            <div
              className={classes.progressFill}
              style={{ width: `${progress}%` }}
            />
          </div>

          <Typography variant="body2" style={{ marginTop: '20px' }}>
            {progress < 100
              ? 'Creating your order...'
              : 'Order created successfully!'}
          </Typography>

          {isProcessing && (
            <>
              <Typography
                variant="caption"
                style={{
                  marginTop: '10px',
                  color: '#f44336',
                  fontSize: '12px',
                  fontWeight: 'bold',
                }}
              >
                Please do not close this window or navigate away.
              </Typography>

              {!isOnline && (
                <Typography
                  variant="caption"
                  style={{
                    marginTop: '5px',
                    color: '#ff9800',
                    fontSize: '12px',
                    fontWeight: 'bold',
                    display: 'block',
                  }}
                >
                  ⚠️ No internet connection detected
                </Typography>
              )}
            </>
          )}
        </div>
      </Modal>

      <Toast
        open={toastAlertOpts.show}
        duration={toastAlertOpts.duration}
        onClose={(event, reason) => {
          if (reason === 'clickaway') {
            return;
          }
          setToastAlertOpts({ ...toastAlertOpts, show: false });
        }}
        severity={toastAlertOpts.severity}
        message={toastAlertOpts.message}
      />
    </div>
  );
};

export default CreateSubscription;
